/**
 * SwapRoutePopup Component
 *
 * Simple modal showing swap route visualization.
 * Displays input token → DEX → output SOL in a clean horizontal flow.
 */

import { X } from 'lucide-react';

import { DUST_SWAP_CONSTANTS } from '@/lib/constants';
import type { TokenPriceData } from '@/hooks/useTokenPrices';

interface TokenInfo {
  mint: string;
  symbol: string;
  name: string;
  logoUrl?: string;
  uiAmount?: number;
}

interface SwapRoutePopupProps {
  open: boolean;
  onClose: () => void;
  token: TokenInfo | null;
  priceData: TokenPriceData | null;
}

function formatAmount(value: number | string, decimals: number = 6): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === 0) return '0';
  if (num < 0.000001) return '<0.000001';
  if (num < 0.0001) return num.toFixed(6);
  if (num < 0.01) return num.toFixed(4);
  if (num < 1000) return num.toLocaleString(undefined, { maximumFractionDigits: decimals });
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatSolValue(lamportsOrSol: number | string, isLamports: boolean = false): string {
  const sol = isLamports
    ? parseInt(String(lamportsOrSol), 10) / 1e9
    : typeof lamportsOrSol === 'string'
      ? parseFloat(lamportsOrSol)
      : lamportsOrSol;

  if (sol === 0) return '0';
  if (sol < 0.000001) return '<0.000001';
  if (sol < 0.0001) return sol.toFixed(6);
  if (sol < 0.01) return sol.toFixed(4);
  return sol.toFixed(4);
}

export function SwapRoutePopup({ open, onClose, token, priceData }: SwapRoutePopupProps) {
  if (!open || !token || !priceData) {
    return null;
  }

  const { quote, estimatedSolValue } = priceData;
  const routePlan = quote?.routePlan || [];

  // Get DEX name from route
  const dexName = routePlan[0]?.swapInfo?.label || 'Jupiter';

  // Calculate fee and net value
  const feeAmount = estimatedSolValue * (DUST_SWAP_CONSTANTS.PLATFORM_FEE_PERCENT / 100);
  const netValue = estimatedSolValue - feeAmount;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl"
        style={{
          backgroundColor: '#0D1117',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <h2
            className="text-lg font-semibold text-white"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Routing
          </h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/10"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Route Visualization */}
        <div className="px-5 pb-6">
          <div
            className="rounded-xl p-6"
            style={{ backgroundColor: '#161B22' }}
          >
            {/* Horizontal Flow: SOL Output ← Line with DEX → Token Input */}
            <div className="flex items-center justify-between">
              {/* Left: SOL Output */}
              <div
                className="flex items-center gap-2 rounded-full px-4 py-2"
                style={{
                  backgroundColor: '#1F2937',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div
                  className="flex h-5 w-5 items-center justify-center rounded-full text-xs"
                  style={{ backgroundColor: '#9945FF' }}
                >
                  ◎
                </div>
                <span
                  className="text-sm font-medium text-white"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {formatSolValue(estimatedSolValue)} SOL
                </span>
              </div>

              {/* Center: Connecting line with DEX name */}
              <div className="relative flex-1 mx-4">
                {/* Line */}
                <div
                  className="absolute top-1/2 left-0 right-0 h-px -translate-y-1/2"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                />
                {/* Vertical connectors */}
                <div
                  className="absolute left-0 top-1/2 h-3 w-px -translate-y-1/2"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                />
                <div
                  className="absolute right-0 top-1/2 h-3 w-px -translate-y-1/2"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                />
                {/* DEX Label */}
                <div className="relative flex justify-center pt-6">
                  <div className="flex items-center gap-1.5 rounded-full bg-[#0D1117] px-3 py-1">
                    {token.logoUrl ? (
                      <img
                        src={token.logoUrl}
                        alt=""
                        className="h-4 w-4 rounded-full"
                        onError={e => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="h-4 w-4 rounded-full bg-gray-600" />
                    )}
                    <span
                      className="text-xs text-gray-400"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {dexName}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: Token Input */}
              <div
                className="flex items-center gap-2 rounded-full px-4 py-2"
                style={{
                  backgroundColor: '#1F2937',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                {token.logoUrl ? (
                  <img
                    src={token.logoUrl}
                    alt={token.symbol}
                    className="h-5 w-5 rounded-full"
                    onError={e => {
                      (e.target as HTMLImageElement).src = '/token-placeholder.svg';
                    }}
                  />
                ) : (
                  <div className="h-5 w-5 rounded-full bg-gray-600" />
                )}
                <span
                  className="text-sm font-medium text-white"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {formatAmount(token.uiAmount || 0, 6)} {token.symbol}
                </span>
              </div>
            </div>
          </div>

          {/* Summary - Minimal */}
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span style={{ fontFamily: 'Outfit, sans-serif', color: '#6B7280' }}>
                Platform fee ({DUST_SWAP_CONSTANTS.PLATFORM_FEE_PERCENT}%)
              </span>
              <span style={{ fontFamily: 'Outfit, sans-serif', color: '#9CA3AF' }}>
                -{formatSolValue(feeAmount)} SOL
              </span>
            </div>
            <div className="flex justify-between">
              <span
                className="text-sm font-medium"
                style={{ fontFamily: 'Outfit, sans-serif', color: '#FFFFFF' }}
              >
                You receive
              </span>
              <span
                className="text-sm font-bold"
                style={{ fontFamily: 'Outfit, sans-serif', color: '#4ADE80' }}
              >
                {formatSolValue(netValue)} SOL
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
